import { useState, type CSSProperties } from "react";
import { useStore } from "../../store/globalState";
import {
  iconCatalog,
  type IconCatalogEntry,
  type IconCatalogTab,
  type IconPaint,
} from "../../icons/catalog";

const TABS: { id: IconCatalogTab; label: string }[] = [
  { id: "icons", label: "Icons" },
  { id: "components", label: "In components" },
  { id: "brands", label: "Brands" },
];

const SIZES = [24, 65] as const;

const depositVars = (paint: IconPaint, background: string): CSSProperties =>
  ({
    "--cdh-ink": paint.ink,
    "--cdh-mid": `color-mix(in srgb, ${paint.ink} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${paint.ink} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${paint.ink} 10%, ${background})`,
    "--cdh-neutral": paint.ink,
    "--cdh-neutral-strong": paint.ink,
    "--cdh-white": background,
    "--cdh-title": paint.ink,
    "--cdh-body": paint.ink,
    "--cdh-muted": paint.ink,
    "--cdh-accent": paint.ink,
    "--cdh-surface": background,
    "--cdh-border": paint.ink,
  }) as CSSProperties;

const solid = (value: string | undefined, fallback: string) =>
  value && !value.includes("gradient") ? value : fallback;

const pair = (
  colors: [string, string] | undefined,
  fallback: [string, string],
): [string, string] => {
  const first = colors?.[0];
  const second = colors?.[1] ?? first;
  if (!first || !second) return fallback;
  return [first, second];
};

const IconGrid = ({
  entries,
  tab,
  copiedId,
  copyId,
  lightPlate,
  darkPlate,
  lightPaint,
  darkPaint,
}: {
  entries: IconCatalogEntry[];
  tab: IconCatalogTab;
  copiedId: string | null;
  copyId: (id: string) => Promise<void>;
  lightPlate: string;
  darkPlate: string;
  lightPaint: (size: number) => IconPaint;
  darkPaint: (size: number) => IconPaint;
}) => (
  <ul className="icon-catalog__grid">
    {entries.map((entry) => (
      <li key={entry.id} className="icon-catalog__card">
        <div className="icon-catalog__previews">
          <div
            className="icon-catalog__plate"
            style={{
              background: lightPlate,
              ...depositVars(lightPaint(24), lightPlate),
            }}
          >
            {SIZES.map((size) => (
              <span key={size}>{entry.render(lightPaint(size))}</span>
            ))}
          </div>
          {tab !== "brands" && (
            <div
              className="icon-catalog__plate"
              style={{
                background: darkPlate,
                ...depositVars(darkPaint(24), darkPlate),
              }}
            >
              {SIZES.map((size) => (
                <span key={size}>{entry.render(darkPaint(size))}</span>
              ))}
            </div>
          )}
        </div>
        <p className="icon-catalog__id">{entry.id}</p>
        <p className="icon-catalog__name">{entry.name}</p>
        {entry.note && <p className="icon-catalog__note">{entry.note}</p>}
        <button
          type="button"
          className="icon-catalog__copy"
          onClick={() => void copyId(entry.id)}
        >
          {copiedId === entry.id ? "Copied" : "Copy id"}
        </button>
      </li>
    ))}
  </ul>
);

export const IconCatalogPage = () => {
  const whiteView = useStore((s) => s.theme.whiteView);
  const specialView = useStore((s) => s.theme.specialView);
  const [tab, setTab] = useState<IconCatalogTab>("icons");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const lightColors = pair(whiteView.iconColors, ["#111111", "#111111"]);
  const darkInk = solid(specialView.textColorBtn, "#ffffff");
  const darkColors: [string, string] = [darkInk, darkInk];
  const lightPlate = whiteView.background || "#ffffff";
  const darkPlate = specialView.background || "#111111";

  const lightPaint = (size: number): IconPaint => ({
    size,
    colors: lightColors,
    ink: lightColors[0],
    glyph: "#ffffff",
    onDark: false,
  });
  const darkPaint = (size: number): IconPaint => ({
    size,
    colors: darkColors,
    ink: darkColors[0],
    glyph: "#1a1a1a",
    onDark: true,
  });

  const entries = iconCatalog.filter(
    (entry) => entry.tab === tab && entry.source !== "lola",
  );
  const lolaEntries =
    tab === "icons" ? iconCatalog.filter((entry) => entry.source === "lola") : [];

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      return;
    } catch {
      const area = document.createElement("textarea");
      area.value = id;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.left = "-9999px";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      setCopiedId(ok ? id : null);
    }
  };

  return (
    <div className="icon-catalog">
      <header className="icon-catalog__header">
        <h1 className="icon-catalog__title">Icon catalog</h1>
        <p className="icon-catalog__lede">
          Canonical drawings. Widgets keep their own copies. Ask for the id
          when placing one.
        </p>
        <div className="icon-catalog__tabs" role="tablist">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`icon-catalog__tab ${tab === item.id ? "is-active" : ""}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      <IconGrid
        entries={entries}
        tab={tab}
        copiedId={copiedId}
        copyId={copyId}
        lightPlate={lightPlate}
        darkPlate={darkPlate}
        lightPaint={lightPaint}
        darkPaint={darkPaint}
      />

      {lolaEntries.length > 0 && (
        <section className="icon-catalog__lola">
          <h2>Viene de Lola</h2>
          <p>
            Imported from lola-framework-ui-test. Use these. Do not copy them
            into assets/icons.
          </p>
          <IconGrid
            entries={lolaEntries}
            tab={tab}
            copiedId={copiedId}
            copyId={copyId}
            lightPlate={lightPlate}
            darkPlate={darkPlate}
            lightPaint={lightPaint}
            darkPaint={darkPaint}
          />
        </section>
      )}
    </div>
  );
};
